import { BrowserRouter, Switch, Route } from "react-router-dom";
import Home from "../Components/Home/Home";
import CompanyInternships from "../Components/Internships/CompanyInternships";
import FacultyInternships from "../Components/Internships/FacultyInternships";
import StudentInternships from "../Components/Internships/StudentInternships";
import Register from "../Components/Register/Register";
import Authenticate from "../Components/Authenticate/Authenticate";
import Profile from "../Components/Profile/Profile";
import Account from "../Components/MyAccount/MyAccount";
import CompanyAccount from "../Components/MyAccount/CompanyAccount";
import StudentProfile from "../Components/MyAccount/StudentProfile";
import Internship from "../Components/InternshipInfo/InternshipInfo";
import Terms from "../Components/Terms/Terms";
import Privacy from "../Components/Privacy/Privacy";
import Questions from "../Components/Questions/Questions";
import Notifications from "../Components/Notifications/Notifications";
import Students from "../Components/Students/Students";
import MyStudents from "../Components/Students/MyStudents";
import MailValidation from "../Components/MailValidation/MailValidation";
import Student from "../Components/Student/Student";
import PracticeNotebook from "../Components/PracticeNotebooks/PracticeNotebook";
import PracticeCertificate from "../Components/PracticeCertificate/PracticeCertificate";
import Users from "../Components/Admin/Users";
import NotFoundPage from "../Components/ErrorPage/NotFoundPage";
import InternshipDetailedView from "../Components/Internships/InternshipDetailedView";
import InternshipDocument from "../Components/Internships/InternshipDocument";
import Documents from "../Components/Admin/Documents";
import CompanyStudentsTable from "../Components/Students/CompanyStudentsTable";
import {
	isUserLogged,
	safeAnd,
	safeOr,
	useIsAdmin,
	useIsAdministrativeAccordsResponsible,
	useIsCompaniesFeedbackEnabled,
	useIsCompany,
	useIsDean,
	useIsDepartmentDirector,
	useIsFacultyAdmin,
	useIsFacultyContactsResponsible,
	useIsFeedbackAdmin,
	useIsInternshipAdmin,
	useIsNewsletterAdmin,
	useIsPhdStudentsFeedbackEnabled,
	useIsProfessor,
	useIsRector,
	useIsStudent,
	useIsStudentsFeedbackEnabled,
	useIsStudyProgramCoordinator,
	useIsUPBAdministrativeUser,
	useIsPoliJobsAdmin,
	useIsMentorshipAdmin,
	useIsInternalStudent,
	useIsUniversityAccordResponsible,
} from "../utils/utilFunctions";
import GuardedRoute from "./GuardedRoute";
import PrivateRoute from "./PrivateRoute";
import Report from "../Components/Report/Report";
import ResetPassword from "../Components/ResetPassword/ResetPassword";
import PracticeStudents from "../Components/PracticeStudents/PracticeStudents";
import ScrollToTop from "../utils/scrollToTop";
import {
	AnnouncementFormPage,
	AnnouncementsPage,
	CompaniesPage,
	FacultySettingsPage,
	CompanyContactsPage,
	DiplomaStudentsPage,
	NewsletterEmailsPage,
	PreviousEnrollmentsPage,
	PublishEmailFormPage,
	ApplicantsPage,
	AddInternshipFormPage,
	FacultiesContactInformationPage,
	VirtualBoardPage,
} from "../Components/Pages";
import AllEvents from "../Components/Events/AllEvents";
import BachelorStudentsForm from "../Components/Feedback/BachelorStudentsForm";
import PhdStudentsForm from "../Components/Feedback/PhdStudentsForm";
import ProfesorsForm from "../Components/Feedback/ProfesorsForm";
import CompaniesForm from "../Components/Feedback/CompaniesForm";
import ProfesorsEvaluationForm from "../Components/Feedback/ProfesorsEvaluationForm";
import AddResearchProposalForm from "../Components/AddResearchProposalForm/AddResearchProposalForm";
import ResearchOffers from "../Components/ResearchOffers/ResearchOffers";
import ResearchProposal from "../Components/ResearchOffers/ResearchProposal";
import { NewsletterEmailInfoPage } from "../Components/Pages/NewsletterEmailInfoPage";
import AdminFeatureFlagsView from "../Components/Feedback/AdminFeatureFlagsView";
import FeatureFlagsView from "../Components/Feedback/FeatureFlagsView";
import { Routes } from "../utils/routes";
import ViewStudent from "../Components/Profile/ViewStudent";
import AddPoliJobsProposalForm from "../Components/AddPoliJobsProposalForm/AddPoliJobsProposalForm";
import PoliJobsProposal from "../Components/PoliJobsOffers/PoliJobsProposal";
import PoliJobsOffers from "../Components/PoliJobsOffers/PoliJobsOffers";
import AdminEnrollments from "../Components/AdminEnrollments/EnrollmentsView";
import StudentApplications from "../Components/PoliJobsOffers/StudentApplications";
import ProfesorsAutoEvaluationForm from "../Components/Feedback/ProfesorsAutoEvaluationForm";
import ProfesorsPeerEvaluationForm from "../Components/Feedback/ProfesorsPeerEvaluationForm";
import MentorshipOffers from "../Components/MentorshipOffers/MentorshipOffers";
import { useFeatureFlags } from "../utils/featureFlags";
import CVAccessSharing from "../Components/CVAccessSharing/CVAccessSharing";
import PoliJobsReport from "../Components/PoliJobsReport/PoliJobsReport";
import { StudentCVsPage } from "../Components/Pages/StudentCVsPage";

const Router = () => {
	const isCompany = useIsCompany();
	const isProfessor = useIsProfessor();
	const isStudent = useIsStudent();
	const isInternalStudent = useIsInternalStudent();
	const isUPBAdministrativeUser = useIsUPBAdministrativeUser();
	const isUniversityAccordResponsible = useIsUniversityAccordResponsible();
	const isInternshipAdmin = useIsInternshipAdmin();
	const isFacultyAdmin = useIsFacultyAdmin();
	const isDepartmentDirector = useIsDepartmentDirector();
	const isDean = useIsDean();
	const isStudyProgramCoordinator = useIsStudyProgramCoordinator();
	const isAdmin = useIsAdmin();
	const isNewsletterAdmin = useIsNewsletterAdmin();
	const isMentorshipAdmin = useIsMentorshipAdmin();
	const isRector = useIsRector();
	const isAdministrativeAccordsResponsible =
		useIsAdministrativeAccordsResponsible();
	const isFacultyContactsResponsible = useIsFacultyContactsResponsible();
	const isPoliJobsAdmin = useIsPoliJobsAdmin();
	const { isMentorshipOffersEnabled, isPoliJobsMarketEnabled } =
		useFeatureFlags();

	const { data: isStudentsFeedbackEnabled } = useIsStudentsFeedbackEnabled();
	const { data: isPhdStudentsFeedbackEnabled } =
		useIsPhdStudentsFeedbackEnabled();
	const { data: isCompaniesFeedbackEnabled } = useIsCompaniesFeedbackEnabled();
	const isFeedbackAdmin = useIsFeedbackAdmin();

	return (
		<BrowserRouter>
			<ScrollToTop />
			<Switch>
				<Route exact path="/" component={Home} />
				<Route path="/termeni" component={Terms} />
				<Route path="/politica&confidentialitate" component={Privacy} />
				<Route path="/intrebari-frecvente" component={Questions} />
				<Route
					path={Routes.FACULTIES_CONTACT}
					component={FacultiesContactInformationPage}
				/>
				<PrivateRoute
					condition={() => isUserLogged()}
					path="/propunere/:id"
					component={Internship}
				/>
				<Route path="/404" component={NotFoundPage} />
				<Route path="/profil" component={Profile} />
				<PrivateRoute
					condition={() => isUserLogged()}
					path={Routes.STUDENT_PROFILE}
					component={ViewStudent}
				/>

				<GuardedRoute exact path="/notificari" component={Notifications} />
				<GuardedRoute exact path="/contul-meu" component={Account} />
				<GuardedRoute exact path="/contul-meu/:userId" component={Account} />
				<GuardedRoute exact path="/student" component={Student} />

				<PrivateRoute
					condition={() => isPoliJobsMarketEnabled}
					path="/partajare-cv/:companyId"
					component={CVAccessSharing}
				/>

				<Route path="/inregistrare" component={Register} />
				<Route path="/autentificare" component={Authenticate} />
				<Route
					path="/mail-de-validare/:validationToken"
					component={MailValidation}
				/>
				<Route
					path="/resetare-parola/:validationToken"
					component={ResetPassword}
				/>
				<PrivateRoute
					condition={() => safeOr([isCompany, isProfessor])}
					path={"/editare-propunere/:internshipId"}
					component={AddInternshipFormPage}
				/>
				<PrivateRoute
					condition={() => safeOr([isCompany, isProfessor])}
					path="/editare-propunere"
					component={AddInternshipFormPage}
				/>
				<PrivateRoute
					condition={() => isInternalStudent}
					path="/aplicarile-mele"
					component={StudentInternships}
				/>
				<PrivateRoute
					condition={() => isInternalStudent}
					path="/aplicarile-mele-polijobs"
					component={StudentApplications}
				/>
				<PrivateRoute
					condition={() => isInternalStudent}
					path="/bursele-mele"
					component={StudentInternships}
				/>
				<PrivateRoute
					condition={() => isInternalStudent}
					path="/lucrare-licenta"
					component={InternshipDetailedView}
				/>
				<PrivateRoute
					condition={() => isInternalStudent}
					path="/tema-cercetare"
					component={InternshipDetailedView}
				/>
				<PrivateRoute
					condition={() => isInternalStudent}
					path="/stagiu-practica"
					component={InternshipDetailedView}
				/>
				<PrivateRoute
					condition={() => isInternalStudent}
					path="/alte-pozitii"
					component={StudentInternships}
				/>
				<PrivateRoute
					condition={() => safeOr([isCompany, isProfessor])}
					path="/aplicanti"
					component={ApplicantsPage}
				/>

				<PrivateRoute
					condition={() => true}
					path="/cv-studenti/:companyId"
					component={StudentCVsPage}
				/>

				<PrivateRoute
					condition={() => safeAnd([isCompany, isPoliJobsMarketEnabled])}
					path="/cv-studenti"
					component={StudentCVsPage}
				/>

				<PrivateRoute
					condition={() =>
						safeAnd([
							isUserLogged(),
							!isUPBAdministrativeUser,
							!isAdmin,
							!isFacultyAdmin,
							!isDean,
							!isDepartmentDirector,
							!isStudent,
							!isCompany,
						])
					}
					path="/studenti"
					component={Students}
				/>
				<PrivateRoute
					condition={() => isCompany}
					path="/studentii-mei"
					component={MyStudents}
				/>
				<PrivateRoute
					condition={() =>
						safeOr([isInternshipAdmin, isFacultyAdmin, isDean, isAdmin])
					}
					path="/studenti-practica"
					component={PracticeStudents}
				/>
				<PrivateRoute
					condition={() =>
						safeOr([
							isCompany,
							isUPBAdministrativeUser,
							isUniversityAccordResponsible,
						])
					}
					path="/acorduri-universitate"
					component={Documents}
				/>
				<PrivateRoute
					condition={() =>
						safeOr([
							isRector,
							isAdmin,
							isDean,
							isFacultyAdmin,
							isDepartmentDirector,
						])
					}
					path="/utilizatori"
					component={Users}
				/>
				<PrivateRoute
					condition={() => isInternalStudent}
					path="/editare-caiet-practica"
					component={PracticeNotebook}
				/>
				<PrivateRoute
					condition={() =>
						safeOr([
							isProfessor,
							isInternalStudent,
							isDean,
							isStudyProgramCoordinator,
							isDepartmentDirector,
						])
					}
					path="/editare-document-propunere"
					component={InternshipDocument}
				/>
				<PrivateRoute
					condition={() =>
						safeOr([isDean, isDepartmentDirector, isAdmin, isFacultyAdmin])
					}
					path="/rapoarte"
					component={Report}
				/>
				<PrivateRoute
					condition={() => isPoliJobsAdmin}
					path="/raport-polijobs"
					component={PoliJobsReport}
				/>
				{/* TODO: add new role */}
				<PrivateRoute
					condition={() =>
						safeOr([
							isDean,
							isFacultyContactsResponsible,
							isRector,
							isAdministrativeAccordsResponsible,
						])
					}
					path="/companii-contact"
					component={CompaniesPage}
				/>
				<PrivateRoute
					condition={() =>
						safeOr([
							isDean,
							isFacultyContactsResponsible,
							isRector,
							isAdministrativeAccordsResponsible,
						])
					}
					path="/contacte/:companyId"
					component={CompanyContactsPage}
				/>
				<PrivateRoute
					condition={() => safeOr([isCompany, isProfessor])}
					path="/editare-atestat-practica"
					component={PracticeCertificate}
				/>
				<PrivateRoute
					condition={() => safeOr([isCompany, isProfessor])}
					path="/propuneri"
					component={isCompany ? CompanyInternships : FacultyInternships}
				/>
				<PrivateRoute
					condition={() => safeOr([isInternalStudent, isFacultyAdmin])}
					path="/propuneri-companii"
					component={CompanyInternships}
				/>
				<PrivateRoute
					condition={() => safeOr([isInternalStudent])}
					path="/propuneri-facultati"
					component={FacultyInternships}
				/>
				<PrivateRoute
					condition={() => safeOr([isInternalStudent])}
					path="/avizier-virtual"
					component={VirtualBoardPage}
				/>
				<PrivateRoute
					condition={() => safeOr([isInternalStudent])}
					path="/evenimente"
					component={AllEvents}
				/>
				<PrivateRoute
					condition={() => isFacultyAdmin || isAdmin || isCompany}
					path="/documente-companie"
					component={Documents}
				/>
				<PrivateRoute
					condition={() => isCompany}
					path="/tabel-studenti"
					component={CompanyStudentsTable}
				/>
				<PrivateRoute
					condition={() => safeOr([isDean])}
					path="/studenti-diploma"
					component={DiplomaStudentsPage}
				/>
				<PrivateRoute
					condition={() => isNewsletterAdmin}
					path="/editare-email/:emailId"
					component={PublishEmailFormPage}
				/>
				<PrivateRoute
					condition={() => isNewsletterAdmin}
					path="/editare-email"
					component={PublishEmailFormPage}
				/>
				<PrivateRoute
					condition={() => isNewsletterAdmin}
					path="/newsletter"
					component={NewsletterEmailsPage}
				/>
				<PrivateRoute
					condition={() => isNewsletterAdmin}
					path="/newsletter-email/:id"
					component={NewsletterEmailInfoPage}
				/>
				<PrivateRoute
					condition={() => isNewsletterAdmin || isAdmin}
					path="/anunturi"
					component={AnnouncementsPage}
				/>
				<PrivateRoute
					condition={() => isNewsletterAdmin || isAdmin}
					path="/editare-anunt/:id"
					component={AnnouncementFormPage}
				/>
				<PrivateRoute
					condition={() => isNewsletterAdmin || isAdmin}
					path="/adaugare-anunt"
					component={AnnouncementFormPage}
				/>
				<PrivateRoute
					condition={() => isInternalStudent}
					path="/inrolari-anterioare"
					component={PreviousEnrollmentsPage}
				/>
				<PrivateRoute
					condition={() => isDean}
					path="/settings"
					component={FacultySettingsPage}
				/>
				<PrivateRoute
					condition={() => safeOr([isCompany, isProfessor])}
					path={"/editare-propunere-cercetare/:researchProposalId"}
					component={AddResearchProposalForm}
				/>
				<PrivateRoute
					condition={() => safeOr([isCompany, isProfessor])}
					path="/editare-propunere-cercetare"
					component={AddResearchProposalForm}
				/>

				<PrivateRoute
					condition={() => safeOr([isCompany, isProfessor])}
					path="/propuneri-cercetare"
					component={ResearchOffers}
				/>
				<Route path="/propunere-cercetare/:id" component={ResearchProposal} />

				{/* PoliJobs offers */}
				<PrivateRoute
					condition={() => isPoliJobsAdmin}
					path={Routes.EDIT_POLIJOBS + "/:poliJobsProposalId"}
					component={AddPoliJobsProposalForm}
				/>
				<PrivateRoute
					condition={() => isPoliJobsAdmin}
					path={Routes.EDIT_POLIJOBS}
					component={AddPoliJobsProposalForm}
				/>

				<PrivateRoute
					condition={() => safeOr([isPoliJobsAdmin, isInternalStudent])}
					path={Routes.POLIJOBS}
					component={PoliJobsOffers}
				/>
				<Route path="/propunere-polijobs/:id" component={PoliJobsProposal} />

				{/* mentorship offers */}
				<PrivateRoute
					condition={() =>
						safeOr([isAdmin, isMentorshipAdmin, isMentorshipOffersEnabled])
					}
					path={Routes.MENTORSHIP_OFFERS}
					component={MentorshipOffers}
				/>

				{/* feedback form routes */}
				<PrivateRoute
					condition={() => isUserLogged() && isStudentsFeedbackEnabled?.value}
					path="/students-feedback"
					component={BachelorStudentsForm}
				/>
				<PrivateRoute
					condition={() =>
						isUserLogged() && isPhdStudentsFeedbackEnabled?.value
					}
					path="/phd-feedback"
					component={PhdStudentsForm}
				/>
				<PrivateRoute
					condition={() => true}
					path="/professor-feedback"
					component={ProfesorsForm}
				/>
				<PrivateRoute
					condition={() => isUserLogged() && isCompaniesFeedbackEnabled?.value}
					path="/companies-feedback"
					component={CompaniesForm}
				/>
				<PrivateRoute
					condition={() => true}
					path="/profesors-evaluation-feedback"
					component={ProfesorsEvaluationForm}
				/>

				<PrivateRoute
					condition={() => true}
					path="/profesors-auto-evaluation-feedback"
					component={ProfesorsAutoEvaluationForm}
				/>

				<PrivateRoute
					condition={() => true}
					path="/profesors-peer-evaluation-feedback"
					component={ProfesorsPeerEvaluationForm}
				/>

				<PrivateRoute
					condition={() => isUserLogged() && isFeedbackAdmin}
					path="/feedback-flags-administration"
					component={AdminFeatureFlagsView}
				/>
				<PrivateRoute
					condition={() => isAdmin}
					path={"/cont-companie/:id"}
					component={CompanyAccount}
				/>
				<PrivateRoute
					condition={() => isProfessor}
					path={"/cont-student"}
					component={StudentProfile}
				/>

				<PrivateRoute
					condition={() => isUserLogged() && isAdmin}
					path={Routes.ADMIN_FLAGS}
					component={FeatureFlagsView}
				/>

				<PrivateRoute
					condition={() => isUserLogged() && isAdmin}
					path={Routes.ADMIN_ENROLLMENTS}
					component={AdminEnrollments}
				/>

				{/*should always be the last one!*/}
				<Route path="*" component={NotFoundPage} />
			</Switch>
		</BrowserRouter>
	);
};

export default Router;
